import React, {useContext, useEffect, useRef, useState} from "react";
import {GisContext} from "../context/gis-context.jsx";
import Button from "../../../../components/button/button.jsx";
import {Modal} from "../../../../components/modal/modal.jsx";
import SelectComponent from "../../../../components/select/select-component.jsx";
import InputForm from "../../../../components/input/input-form.jsx";
import {FaAnglesDown} from "react-icons/fa6";
import {FaBell} from "react-icons/fa";
import {WazeSubType, WazeType} from "../../../../utils/waze-types.js";
import {getAssetPath} from "../../../../utils/get-asset-path.js";
import InputCheckbox from "../../../../components/input/input-checkbox.jsx";
import {useMapsLibrary} from "@vis.gl/react-google-maps";
import gisUtils from "../utils/gis-utils.jsx";

const GisCardList = ({map, events, markers, geoItems, geoLayers, filterTarget, handleDetails, targetRef, markerRef, open, setOpen}) => {
    const {target, setGeoLayerChecked, geoLayerChecked, menu, changeMenu, drawingMode, setDrawingMode} = useContext(GisContext);

    const coreLib = useMapsLibrary("core");
    const markerLib = useMapsLibrary("marker");

    const filterRef = useRef("");

    const listRef = useRef(null);
    const selectedItemRef = useRef(null);
    const sortedIdsRef = useRef([]);

    const [maxItens, setMaxItens] = useState(50);
    const [search, setSearch] = useState("");
    const [options, setOptions] = useState([]);
    const [selectedOptions, setSelectedOptions] = useState([]);

    useEffect(() => {
        if (targetRef.current !== target.selected) {
            setSearch("");
            setSelectedOptions([]);
        }

        getTargetOptions();
    }, [target]);

    useEffect(() => setMaxItens(50), [search, selectedOptions]);

    useEffect(() => {
        const selectedId = target.eventSelected?.id;
        if (!selectedId || target.selected === "zone") return;

        const index = sortedIdsRef.current.indexOf(selectedId);
        if (index === -1) return;

        if (index >= maxItens) {
            setMaxItens(Math.ceil((index + 1) / 50) * 50);
            return;
        }

        scrollToSelectedItem();
    }, [target.eventSelected?.id, target.selected, maxItens]);

    const scrollToSelectedItem = () => {
        const container = listRef.current;
        const item = selectedItemRef.current;
        if (!container || !item) return;

        const containerRect = container.getBoundingClientRect();
        const itemRect = item.getBoundingClientRect();

        const itemTop = itemRect.top - containerRect.top + container.scrollTop;
        const itemBottom = itemTop + itemRect.height;

        const isVisible = itemTop >= container.scrollTop && itemBottom <= container.scrollTop + container.clientHeight;
        if (isVisible) return;

        container.scrollTo({
            top: Math.max(0, itemTop - (container.clientHeight - itemRect.height) / 2),
            behavior: "smooth"
        });
    }

    useEffect(() => {
        if (filterTarget && filterTarget !== filterRef.current) {
            setSearch("");
            setSelectedOptions([]);

            filterRef.current = filterTarget;
        }
    }, [filterTarget]);

    const getTargetOptions = () => {
        let newOptions = [];

        switch (target.selected) {
            case "attendance":
                newOptions = [
                    {value: "onHold", label: "Aberto"},
                    {value: "schedule", label: "Agendado"},
                    {value: "inProgress", label: "Em andamento"},
                    {value: "delayed", label: "Atraso"},
                    {value: "assignment", label: "Atribuído"},
                    {value: "panic", label: "Pânico"},
                ];
                break
            case "garrison":
                newOptions = [
                    {value: "dispatchSupport", label: "Apoio"},
                    {value: "dispatchActivity", label: "Atividade"},
                    {value: "dispatch", label: "Despachado"},
                    {value: "available", label: "Disponível"},
                    {value: "panic", label: "Pânico"},
                ];
                break
            case "capturePlace":
                newOptions = [
                    {value: "warning", label: "Alerta"},
                    {value: "offline", label: "Offline"},
                    {value: "ok", label: "Online"},
                ];
                break
            case "contactid":
                newOptions = [
                    {value: "Armado", label: "Armado"},
                    {value: "Desarmado", label: "Desarmado"},
                    {value: "Offline", label: "Offline"},
                ];
                break
            case "sos":
                newOptions = [
                    {value: "AGGRESSOR", label: "Agressor"},
                    {value: "CITIZEN", label: "Protegido"},
                ];
                break
            case "vms":
                newOptions = [
                    {value: "00C7BE", label: "Azul"},
                    {value: "B46900", label: "Marrom"},
                    {value: "default", label: "Padrão"},
                    {value: "FF7D96", label: "Rosa"},
                    {value: "AF52DE", label: "Roxo"},
                ];
                break
        }

        setOptions(newOptions);
    }

    const handleSelect = (value) => setSelectedOptions(value);

    const closeModal = () => setOpen(false);

    const handleMarker = (eventId) => {
        const marker = markers.get(eventId) ?? geoItems.get(eventId);
        if (!marker) return;

        marker.setZIndex(99);
        marker.setVisible(true);
        marker.setAnimation(markerLib.Animation.BOUNCE);

        const event = marker.type !== "point" ? events.get(eventId) : marker;

        map.setZoom(18);
        map.setCenter(marker.getPosition());
        handleDetails(marker.type, event, false);
    }

    const handleGeoLayer = (element) => {
        const {checked, id} = element.target;

        let newMenu = {...menu};
        let newChecked = [...geoLayerChecked];
        const eventId = id.replace("check-", "");

        if (eventId.includes("ZONE")) {
            geoLayers.get(eventId).forEach(layer => layer.setVisible(checked));
        } else {
            [...geoItems].filter(([, marker]) => marker.id_ === eventId && markerRef.current !== marker.id)
                .forEach(([, marker]) => marker.setVisible(checked));
        }

        if (checked) newChecked.push(eventId);
        else newChecked = newChecked.filter(item => item !== eventId);

        if (eventId.includes("ZONE")) {
            if (newChecked.includes(eventId)) {
                const bounds = new coreLib.LatLngBounds();
                events.get(eventId).details.layers.forEach(event => {
                    for (let i = 0; i < event.coordinates.length; i += 2) {
                        const lat = Number(event.coordinates[i]);
                        const lng = Number(event.coordinates[i + 1]);
                        bounds.extend(new coreLib.LatLng(lat, lng));
                    }
                });

                map.fitBounds(bounds);
            }

            newMenu.zone.show = newChecked.some(item => item.includes("ZONE"));
        }

        if (eventId.includes("POINT")) {
            const bool = newChecked.some(item => item.includes("POINT"));
            newMenu.point.show = bool;

            if (bool) {
                const find = Object.keys(newMenu).find(key => key !== target.selected && !!newMenu[key]?.highlight);
                if (find) newMenu[find].highlight = false;
            }

        }

        changeMenu(newMenu);
        setGeoLayerChecked(newChecked);
    }

    const selectAllGeoItems = (element, allGeoItems) => {
        const {checked} = element.target;

        const checkedWithoutPoint = [...geoLayerChecked].filter(item => !allGeoItems.includes(item));

        [...geoItems].filter(([, marker]) => markerRef.current !== marker.id).forEach(([, marker]) => marker.setVisible(checked))

        let newMenu = {...menu};
        const newChecked = checked ? [...checkedWithoutPoint, ...allGeoItems] : checkedWithoutPoint;

        newMenu.point.show = checked;

        changeMenu(newMenu);
        setGeoLayerChecked(newChecked);
    }

    const renderGeoItemsList = () => {
        const eventsFilter = [...events].filter(([, event]) => event.type === "point");
        const allGeoItems = eventsFilter.map(([, event]) => event.id);

        const selectAll = allGeoItems.every(item => geoLayerChecked.includes(item));

        return (
            <>
                <li className={"geolayer-items cursor-pointer"}>
                    <InputCheckbox
                        id={"check-select-all"}
                        checked={selectAll}
                        onChange={(e) => selectAllGeoItems(e, allGeoItems)}
                        label="Selecionar todos"
                    />
                </li>
                {
                    eventsFilter.map(([, event]) => (
                            <li className={"geolayer-items cursor-pointer"} key={event.id}>
                                <InputCheckbox
                                    id={"check-" + event.id}
                                    checked={geoLayerChecked?.includes(event.id)}
                                    onChange={handleGeoLayer}
                                    label={
                                        <div className="flex items-center">
                                            <div className="icon">
                                                <img
                                                    className={"h-[30px]"}
                                                    src={getAssetPath("img/markers/customLayers/normal/" + event.icon)}
                                                    alt={event.title}/>
                                            </div>
                                            {event.title}
                                        </div>
                                    }
                                />
                            </li>
                        ))
                }
            </>
        )
    }

    const renderZoneList = () => {
        const valueCleaned = clearString(search).toLowerCase();

        return [...events].filter(([, e]) => {
            if (e.type !== "zone") return false;
            const titleCleaned = clearString(e.title).toLowerCase();
            return titleCleaned.includes(valueCleaned)
        })
            .map(([, e]) =>(
                <li className={"geolayer-items cursor-pointer"} key={e.id}>
                    <InputCheckbox
                        id={"check-" + e.id}
                        checked={geoLayerChecked?.includes(e.id)}
                        onChange={handleGeoLayer}
                        label={
                            <div className="flex items-center">
                                <div className="icon">
                                    <img
                                        src={getAssetPath("img/markers/customLayers/normal/zonas.svg")}
                                        alt={e.title}/>
                                </div>
                                {e.title}
                            </div>
                        }
                    />
                </li>
            ));
    }

    const verifyFilters = (marker) => {
        if (marker?.type_ === "geolayer") return true;

        let type;
        const event = events.get(marker.id);

        const filterPrepared = selectedOptions.map(item => item.value);

        switch (event.type) {
            case "capturePlace":
            case "attendance": type = event.icon;
                break;
            case "garrison": type = event.garrisonType;
                break
            case "contactid": type = event.details.status;
                break;
            case "sos": type = event.details.type;
                break;
            case "vms": type = event.details?.color ?? "default";
                break;
        }

        return !selectedOptions.length || filterPrepared.includes(type);
    }

    const clearString = (string) => string.toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '');

    const renderList = () => {
        const valueCleaned = clearString(search).toLowerCase();

        let ArrayMap = [...markers];
        if (target.selected === "point") ArrayMap = [...geoItems];

        const filterArray = ArrayMap.filter(([, marker]) => {
            let {title, type, details} = marker;
            if (type !== target.selected) return false;
            if (type === "point" && !geoLayerChecked.includes(marker.id_)) return false;
            if (type === "waze") title = WazeType[details.type] + (details?.subType && " - " + WazeSubType[details.subType]);

            const titleCleaned = clearString(title).toLowerCase();
            if (type === "capturePlace") {
                const includesLaneId = details.some(({id}) => clearString(id).toLowerCase().includes(valueCleaned))

                return (titleCleaned.includes(valueCleaned) || includesLaneId) && verifyFilters(marker);
            }

            return titleCleaned.includes(valueCleaned) && verifyFilters(marker);
        });

        const total = filterArray.length;

        const sortedList = filterArray.map(([, marker]) => {
            let {title, id, type, iconTree, details, alarm} = marker;

            if (type === "contactid" && details?.fireAlarm) iconTree = `${type}/fire_alarm-offline`;
            else iconTree = `${type}/${iconTree.replace('-nosignal', '')}`;

            if (type === "waze") title = WazeType[details.type] + (details?.subType && " - " + WazeSubType[details.subType]);

            let icon, isVideoCamera = false;
            switch (type) {
                case 'point':
                    icon = getAssetPath(`img/markers/customLayers/normal/${marker.iconTree}`);
                    break;
                case 'vms':
                    if (details?.color) {
                        const iconColor = gisUtils.vmsCameraIcon(details?.color)
                        icon = getAssetPath(`img/video-cameras/${iconColor}/selector.svg`);
                    } else {
                        icon = getAssetPath(`img/markers-gis/${iconTree}-tree.png`);
                    }
                    break;
                default:
                    icon = getAssetPath(`img/markers-gis/${iconTree}-tree.png`);
                    break;
            }

            return {title, id, type, icon, alarm, isVideoCamera}
        }).sort((a, b) => {
            if (a.alarm !== b.alarm) return a.alarm ? -1 : 1;

            const aOnHold = a.icon.includes("onHold");
            const bOnHold = b.icon.includes("onHold");
            if (aOnHold !== bOnHold) return aOnHold ? -1 : 1;

            return a.title.localeCompare(b.title);
        });

        sortedIdsRef.current = sortedList.map(item => item.id);

        const listFormatted = sortedList.slice(0, maxItens);

        const listHtml = listFormatted.map(item => (
            <div className={`search-items flex justify-between w-full ${target.eventSelected?.id === item.id ? "selected" : ""} ${item.isVideoCamera ? "video-camera"  : ""}`}
                 onClick={() => handleMarker(item.id)}
                 ref={target.eventSelected?.id === item.id ? selectedItemRef : null}
                 key={item.id}>
                <section className={"flex gap-1 items-center w-full"}>
                    <div className="icon">
                        <img src={item.icon} alt={item.type}/>
                    </div>

                    <span className="truncate">{item.title}</span>
                </section>
                {
                    item.alarm &&
                    <FaBell color={"red"}/>
                }
            </div>
        ));

        if (total > maxItens) {
            listHtml.push(
                <div className={"search-items justify-center"} key={"load-more"}
                     onClick={() => setMaxItens(prev => prev + 50)}>
                    Ver mais <FaAnglesDown />
                </div>
            )
        }

        return listHtml?.length ? listHtml :
            <div className="p-2 bg-[#a7d4ff] rounded text-center text-[13px] m-1">
                Nenhum resultado encontrado {target.selected === "point" ? "ou ponto selecionado" : ""}
            </div>;
    }

    const activeDrawingMode = () => {
        setDrawingMode(!drawingMode)
    }

    const pointSelected = geoLayerChecked.filter(item => item.includes("POINT")).length;

    return (
        <>
            {
                filterTarget === "search" &&
                <InputForm
                    id="search" 
                    type="search"
                    value={search}
                    autoFocus={true}
                    label={"Pesquisa"}
                    onChange={({target}) => setSearch(target.value)}
                    classNameWrapper="mt-3"
                    labelClass="search-label"
                />
            }

            {
                filterTarget === "select" &&
                <>
                    <div className="mt-3">
                        <SelectComponent
                            isMulti
                            options={options}
                            value={selectedOptions}
                            label="Selecione um filtro"
                            onChange={handleSelect}
                        />
                    </div>
                </>
            }

            <hr className={"text-[#bdbdbd]"}/>

            {
                target.selected === "point" &&
                <>
                    <Button target="default" onClick={() => setOpen(true)}>
                        Selecionar pontos
                        {
                            pointSelected ?
                                <div className='bg-[var(--blue-dark)] text-white h-[15px] w-[15px] text-center place-content-center text-[11px] rounded-[100%]'>
                                    {pointSelected}
                                </div> : ""
                        }
                    </Button>

                    <Modal id="modal-point" open={open} setOpen={closeModal} overflow={true} title={"Seleção de pontos"} className={"w-6/12"}>
                        <Modal.Body>
                            <ul className="point-list mb-3">
                                {renderGeoItemsList()}
                            </ul>
                        </Modal.Body>
                        <Modal.Footer>
                            <Button target="secondary" onClick={closeModal}>
                                Fechar
                            </Button>
                        </Modal.Footer>
                    </Modal>
                </>
            }

            <div id="list" ref={listRef}>
                {target.selected === "zone" ? renderZoneList() : renderList()}
            </div>
            {
                target.selected === "vms" &&
                <div className={"mb-2"}>
                    <Button className={"btn btn-primary w-full mt-1 mb-1"} onClick={activeDrawingMode}>
                        <span>Mosaico</span>
                    </Button>
                </div>
            }
        </>
    )
}

export default GisCardList;